import { stringify } from 'querystring'

import React from 'react'
import Head from 'next/head'
import clsx from 'clsx'
import { useRouter } from 'next/router'
import dayjs from 'dayjs'
import { Icon } from '@material-ui/core'

import { Icon as SeragamIcon } from '@wartek-id/icon'
import { Text } from '@wartek-id/text'
import { Avatar } from '@wartek-id/avatar'
import { Dialog, DialogContent, DialogOverlay } from '@wartek-id/dialog'

import MateriVideo from 'app/PelatihanMandiriPage/pages/MateriVideo'
import ModuleDetailsPlaceholder from '../partials/Module/ModuleDetailsPlaceholder'
import PostTestCard from '../partials/Module/PostTestCard'
import { GATrackEvent } from 'utils/googleAnalytics'
import PostTest from 'app/PelatihanMandiriPage/pages/PostTest'
import CheckmarkIcon from '../components/CheckmarkIcon'
import MaterialList from '../components/MaterialList'

import type {
  PostTestState,
  MaterialDataResponse,
  ResponseModulData,
} from 'types/microlearning'
import type { CommonListResponse, CommonResponse } from 'types/api'

import ArrowAltRight from 'assets/icons/arrow-alt-right-o.svg'

type ModuleDetailsDesktopProps = {
  modulePreview: CommonResponse<ResponseModulData>
  isLoading: boolean
  isSuccess: boolean
  dialogType: string
  materialData: CommonListResponse<MaterialDataResponse, any>
  toggleShowDetail?: () => void
  handleDialogClose?: () => void
  toggleMateriExpand: (string) => void
  isDrawerOpen: boolean
  lastExpandedIds: string[]
}

const generatePostTestUrl = (
  modulId: string,
  modulName: string,
  topikId: string,
  topikName: string,
  postTestState: PostTestState,
  from: string = '',
  menuId: string = ''
) => {
  let baseUrl = `/pelatihan-mandiri/modul/${modulId}/post-test`

  if (postTestState === 'passed') {
    baseUrl = `${baseUrl}/passed`
  }

  return `${baseUrl}?${stringify({
    topik: topikId,
    topik_name: topikName,
    module_name: modulName,
    postTest: true,
    from: from,
    menuId: menuId,
    testPassed: postTestState === 'passed',
  })}`
}

const ModuleDetailsDesktop = ({
  modulePreview,
  isLoading,
  isSuccess,
  dialogType,
  materialData,
  toggleShowDetail = () => {},
  handleDialogClose = () => {},
  isDrawerOpen = false,
  toggleMateriExpand = () => {},
  lastExpandedIds = [],
}: ModuleDetailsDesktopProps) => {
  const containerRef = React.useRef(null)
  const { query, back } = useRouter()

  const RenderDetailModuleContent = () => (
    <div className="p-4 pb-6">
      <div className="flex flex-col">
        <div className="flex flex-row items-center justify-between">
          <Text>MODUL</Text>

          <button
            onClick={toggleShowDetail}
            className="flex justify-center items-center border-none bg-transparent focus:outline-none"
            type="button"
          >
            <Icon>close</Icon>
          </button>
        </div>
        <Text variant="heading-sm" className="mt-4">
          {modulePreview.data.name}
        </Text>

        <Text
          as="div"
          className="simple-wysiwyg-content mb-6 mt-2"
          dangerouslySetInnerHTML={{
            __html: modulePreview.data.description,
          }}
        />

        {modulePreview.data.learningMaterial?.materials?.length > 0 && (
          <div className="mb-6">
            <div>
              <Text variant="heading-sm">Daftar Materi:</Text>
            </div>
            <Text as="p">
              <ul className="list-disc pl-5">
                {modulePreview.data.learningMaterial?.materials.map(
                  (materi) => {
                    return <li key={materi.id}>{materi.name}</li>
                  }
                )}
              </ul>
            </Text>
          </div>
        )}

        <div className="mb-6">
          <div>
            <Text as="span" variant="heading-sm">
              Langkah Penyelesaian Modul
            </Text>
          </div>
          <Text as="p">
            <ol className="list-decimal pl-5">
              <li>Belajar materi</li>
              <li>Refleksi pembelajaran</li>
              <li>Post Test</li>
            </ol>
          </Text>
        </div>

        <hr className="my-4" />
        <div className="flex flex-row items-center">
          <Avatar
            alt=""
            shape="circle"
            size="lg"
            src={modulePreview.data.reviewer?.imageUrl}
          />

          <Text variant="body-sm" className="ml-4">
            Ditinjau oleh {modulePreview.data.reviewer?.name}
          </Text>
        </div>
      </div>
    </div>
  )

  const RenderVideoFrameContent = () => (
    <div className="">
      <div className="flex flex-row justify-between">
        <div>
          {/* src="/pelatihan-mandiri/video/6588?materi=249&modul=121" */}
          <MateriVideo
            modul={query.modul as string}
            materi={query.materi as string}
            id={query.videoId as string}
            topik={query.topik as string}
            topik_name={query.topik_name as string}
          />
        </div>

        <div className="flex flex-col p-4">
          <div className="flex flex-row items-center justify-between">
            <button
              onClick={handleDialogClose}
              className="flex justify-center items-center border-none bg-transparent focus:outline-none"
              type="button"
            >
              <Icon>close</Icon>
            </button>
          </div>
        </div>
      </div>
    </div>
  )

  const RenderPostTestContent = () => (
    <div>
      <PostTest />
    </div>
  )

  return (
    <>
      <Head>
        <title>Pelatihan Mandiri | Topik {query.topik_name ?? ''}</title>
      </Head>
      <div className="flex flex-row">
        {query.from && (
          <SeragamIcon as="i" className="mr-2" onClick={back}>
            arrow_back
          </SeragamIcon>
        )}
        <div>
          <Text className={clsx('pb-1')}>MODUL</Text>
          <Text className={clsx('pb-3')} variant="heading-lg">
            {modulePreview?.data?.name}
          </Text>
        </div>
      </div>
      <div
        ref={containerRef}
        style={{
          minHeight: 'calc(100vh - 3.5rem)',
        }}
      >
        {isLoading && <ModuleDetailsPlaceholder />}

        {isSuccess && (
          <>
            <div data-testid="success-fetch" className="relative z-0 bg-white">
              <div className="p-4 pb-6">
                <div className="flex flex-row justify-between">
                  <div className="flex flex-row items-center">
                    <div className="w-[3vw]">
                      <Avatar
                        alt=""
                        shape="circle"
                        size="lg"
                        src={modulePreview.data.reviewer?.imageUrl}
                      />
                    </div>

                    <Text variant="body-sm" className="ml-4">
                      Ditinjau oleh {modulePreview.data.reviewer?.name}
                    </Text>
                  </div>
                  <button
                    data-testid="toggle-detail"
                    onClick={toggleShowDetail}
                    className="flex justify-center items-center border-none bg-transparent focus:outline-none"
                    type="button"
                  >
                    <Text variant="action" className="mr-1 underline w-[10vw]">
                      Baca detail modul
                    </Text>
                    <ArrowAltRight className="stroke-current" />
                  </button>
                </div>

                <hr className="my-4" />

                <MaterialList
                  materialList={materialData?.data}
                  query={query}
                  toggleMateriExpand={toggleMateriExpand}
                  lastExpandedIds={lastExpandedIds}
                />
                <Text className="mb-2 mt-6" variant="heading-sm">
                  Post Test
                </Text>
                <div className="mb-4">
                  <PostTestCard
                    href={generatePostTestUrl(
                      String(query.modul),
                      String(modulePreview.data.name),
                      String(query.topik),
                      String(query.topik_name),
                      modulePreview.data.postTest?.state,
                      query.from as string,
                      query.menuId as string
                    )}
                    title="Kerjakan post test"
                    description={
                      modulePreview.data.postTest?.state === 'not_passed'
                        ? 'Post test perlu diulang'
                        : 'Evaluasi pemahaman modul'
                    }
                    isLocked={!modulePreview.data.learningMaterial?.completed}
                    icon={
                      <CheckmarkIcon
                        postTestState={modulePreview.data.postTest?.state}
                      />
                    }
                    trackStep={() =>
                      GATrackEvent({
                        eventName: 'learning_activity',
                        eventParams: {
                          event_trigger: 'click_module_activity_card',
                          action_type: 'click_module_activity',
                          meta: JSON.stringify({
                            topik_id: String(query.topik),
                            module_id: String(query.modul),
                            type: 'post_test',
                          }),
                        },
                        sendDefaultParams: true,
                      })
                    }
                  />
                </div>
                {modulePreview.data.postTest?.state === 'not_passed' && (
                  <div className="mb-4">
                    <PostTestCard
                      href={`/${
                        process.env.NEXT_PUBLIC_TEMP_PELATIHAN_MANDIRI_PATH ||
                        'pelatihan-mandiri'
                      }/modul/${query.modul}/post-test-history?${stringify({
                        topik: query.topik,
                        topik_name: query.topik_name,
                      })}`}
                      title="Hasil post test terakhir"
                      description={dayjs().format('DD/MM/YYYY')}
                      isLocked={!modulePreview.data.learningMaterial?.completed}
                      icon={<Icon>history</Icon>}
                      trackStep={() =>
                        GATrackEvent({
                          eventName: 'learning_activity',
                          eventParams: {
                            event_trigger: 'click_module_activity_card',
                            action_type: 'click_module_activity',
                            meta: JSON.stringify({
                              topik_id: String(query.topik),
                              module_id: String(query.modul),
                              type: 'post_test',
                            }),
                          },
                          sendDefaultParams: true,
                        })
                      }
                    />
                  </div>
                )}
              </div>
            </div>
            {/* TODO: adjust modal for desktop */}
            <Dialog
              isOpen={isDrawerOpen}
              size={
                dialogType === 'video' || dialogType === 'post-test'
                  ? 'lg'
                  : 'sm'
              }
              // @ts-ignore it do support customSize string
              customSize={dialogType === 'video' ? '85vw' : 0}
              onClose={handleDialogClose}
              closeOnOverlayClick={false}
            >
              <DialogOverlay />
              <DialogContent>
                {dialogType === 'video' && <RenderVideoFrameContent />}
                {dialogType === 'module-detail' && (
                  <RenderDetailModuleContent />
                )}
                {dialogType === 'post-test' && <RenderPostTestContent />}
              </DialogContent>
            </Dialog>
          </>
        )}
      </div>
    </>
  )
}

export default ModuleDetailsDesktop
